import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarCheck, Star, ChevronRight, X } from "lucide-react";
import { FaAmazon, FaWhatsapp } from "react-icons/fa";
import { LINKS, openExternal, waIntent, WHATSAPP_DIGITS } from "@/constants/links";
import useFeatures from "@/constants/useFeatures";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const LINK_ACTIONS = [
  {
    testId: "btn-amazon",
    feature: "shop",
    href: LINKS.amazon,
    icon: FaAmazon,
    label: "Acquista su Amazon",
    sub: "Tramite il mio link affiliato",
    cls: "bg-amber-400 text-zinc-950 shadow-[0_0_24px_rgba(251,191,36,0.25)] hover:bg-amber-300",
  },
  {
    testId: "btn-reviews",
    feature: "recensioni",
    href: LINKS.reviews,
    onClick: openExternal(
      LINKS.reviews,
      waIntent(WHATSAPP_DIGITS, "Ciao! Voglio lasciare una recensione del servizio")
    ),
    icon: Star,
    label: "Lascia una Recensione",
    sub: "Racconta la tua esperienza",
    cls: "bg-blue-500 text-white shadow-[0_0_24px_rgba(59,130,246,0.25)] hover:bg-blue-400",
  },
];

export default function ActionButtons({ onBooking }) {
  const features = useFeatures();
  const [reviewsUrl, setReviewsUrl] = useState("");
  const [waContacts, setWaContacts] = useState([]);
  const [showWaChoice, setShowWaChoice] = useState(false);

  useEffect(() => {
    fetch(`${BACKEND}/api/settings/reviews-url`)
      .then((r) => r.json())
      .then((d) => setReviewsUrl(d.url || ""))
      .catch(() => {});
    fetch(`${BACKEND}/api/settings/wa-contacts`)
      .then((r) => r.json())
      .then((rows) => setWaContacts(Array.isArray(rows) ? rows : []))
      .catch(() => {});
  }, []);

  const actions = LINK_ACTIONS.map((a) =>
    a.testId === "btn-reviews" && reviewsUrl
      ? { ...a, href: reviewsUrl, onClick: undefined, sub: "Su Google Maps" }
      : a
  );

  // Contactos WhatsApp configurables desde el admin (socio, jefe, quien sea)
  const waList = waContacts.length
    ? waContacts
    : [{ name: "Socio", phone: WHATSAPP_DIGITS }];
  const hasChoice = waList.length >= 2;
  const waHref = (c) => `https://wa.me/${c.phone}?text=${encodeURIComponent(`Ciao ${c.name}!`)}`;

  return (
    <div className="flex flex-col gap-4" data-testid="action-buttons">
      {(features.chat || features.sumup || features.diretta) && (
        <button
          data-testid="btn-booking"
          onClick={onBooking}
          className="flex w-full items-center gap-4 rounded-2xl bg-emerald-500 px-5 py-4 text-left text-white shadow-[0_0_24px_rgba(16,185,129,0.25)] transition-[background-color,transform] duration-200 hover:bg-emerald-400 active:scale-[0.97]"
        >
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-black/15">
            <CalendarCheck size={22} />
          </span>
          <span className="flex-1 text-left">
            <span className="block text-base font-extrabold leading-tight">Prenota Appuntamento</span>
            <span className="block text-xs font-medium opacity-80">
              Con Jordan, l'assistente del salone
            </span>
          </span>
          <ChevronRight size={20} className="opacity-70" />
        </button>
      )}
      {actions.filter((a) => features[a.feature]).map(({ testId, href, onClick, icon: Icon, label, sub, cls }) => (
        <a
          key={testId}
          data-testid={testId}
          href={href}
          onClick={onClick}
          target="_blank"
          rel="noopener noreferrer"
          className={`flex items-center gap-4 rounded-2xl px-5 py-4 transition-[background-color,transform] duration-200 active:scale-[0.97] ${cls}`}
        >
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-black/15">
            <Icon size={22} />
          </span>
          <span className="flex-1 text-left">
            <span className="block text-base font-extrabold leading-tight">{label}</span>
            <span className="block text-xs font-medium opacity-80">{sub}</span>
          </span>
          <ChevronRight size={20} className="opacity-70" />
        </a>
      ))}

      {features.whatsapp && (
        hasChoice ? (
          <button
            data-testid="btn-whatsapp"
            onClick={() => setShowWaChoice(true)}
            className="flex items-center gap-4 rounded-2xl bg-[#25D366] px-5 py-4 text-left text-white shadow-[0_0_24px_rgba(37,211,102,0.25)] transition-[background-color,transform] duration-200 hover:bg-[#2ee06f] active:scale-[0.97]"
          >
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-black/15">
              <FaWhatsapp size={22} />
            </span>
            <span className="flex-1 text-left">
              <span className="block text-base font-extrabold leading-tight">Scrivici su WhatsApp</span>
              <span className="block text-xs font-medium opacity-80">Risposta rapida, sempre</span>
            </span>
            <ChevronRight size={20} className="opacity-70" />
          </button>
        ) : (
          <a
            data-testid="btn-whatsapp"
            href={waHref(waList[0])}
            onClick={openExternal(waHref(waList[0]), waIntent(waList[0].phone))}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-4 rounded-2xl bg-[#25D366] px-5 py-4 text-white shadow-[0_0_24px_rgba(37,211,102,0.25)] transition-[background-color,transform] duration-200 hover:bg-[#2ee06f] active:scale-[0.97]"
          >
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-black/15">
              <FaWhatsapp size={22} />
            </span>
            <span className="flex-1 text-left">
              <span className="block text-base font-extrabold leading-tight">Scrivici su WhatsApp</span>
              <span className="block text-xs font-medium opacity-80">Risposta rapida, sempre</span>
            </span>
            <ChevronRight size={20} className="opacity-70" />
          </a>
        )
      )}

      <AnimatePresence>
        {showWaChoice && (
          <motion.div
            data-testid="wa-choice-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowWaChoice(false)}
            className="fixed inset-0 z-[80] flex items-end justify-center bg-zinc-950/70 backdrop-blur-sm"
          >
            <motion.div
              initial={{ y: 90 }}
              animate={{ y: 0 }}
              exit={{ y: 90 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="mx-auto w-full max-w-md rounded-t-3xl border-t border-white/10 bg-zinc-900 p-6 pb-10"
            >
              <div className="mb-4 flex items-center justify-between">
                <h3 className="font-display text-xl font-bold text-white">Chi vuoi contattare?</h3>
                <button
                  data-testid="wa-choice-close"
                  onClick={() => setShowWaChoice(false)}
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-white/5 text-zinc-400 hover:bg-white/10 hover:text-white"
                >
                  <X size={17} />
                </button>
              </div>
              <div className="flex flex-col gap-3">
                {waList.map((c, ci) => (
                  <a
                    key={`${c.phone}-${ci}`}
                    data-testid={`wa-choice-${ci === 0 ? "owner" : `contact-${ci}`}`}
                    href={waHref(c)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center gap-4 rounded-2xl px-5 py-4 text-white transition-[background-color,transform] duration-200 active:scale-[0.97] ${
                      ci === 0 ? "bg-[#25D366] hover:bg-[#2ee06f]" : "bg-emerald-600 hover:bg-emerald-500"
                    }`}
                  >
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-black/15">
                      <FaWhatsapp size={22} />
                    </span>
                    <span className="flex-1 text-left">
                      <span className="block text-base font-extrabold leading-tight">{c.name}</span>
                      <span className="block text-xs font-medium opacity-80">+{c.phone}</span>
                    </span>
                  </a>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
